import { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatDateToBEFormatDateFns } from 'utils/date'
import {
  useCreateConfiguredServiceEventsMutation,
  useDeleteConfiguredServiceEventsMutation,
  useLazyFetchConfiguredServiceEventsQuery,
  useUpdateConfiguredServiceEventsMutation,
} from 'api/configured-service/configuredServiceCrud'
import {
  resetState,
  selectSelectedDate,
  selectIsAddingEvent,
  selectItemsMarkedForDeletion,
  selectEditableItems,
} from 'data/configured-service/configuredServiceEventsSlice'
import { selectIsDraftService } from 'data/configured-service/configuredServiceDetailsSelectors'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import { get, keyBy } from 'lodash'
import { handleError } from 'utils/error'
import { getAvailableActionsForEvent as getAvailableActionsForEventUtil } from '../utils/eventUtils'

export const useEventsAccordion = ({ id: explicitId } = {}) => {
  const dispatch = useDispatch()
  const { data } = useCSDetailsCachedData({ id: explicitId })
  const selectedDate = useSelector(selectSelectedDate)
  const isAddingEvent = useSelector(selectIsAddingEvent)
  const itemsMarkedForDeletion = useSelector(selectItemsMarkedForDeletion)
  const editableItems = useSelector(selectEditableItems)
  const isDraft = useMemo(() => selectIsDraftService(data), [data])

  const [updateCSEvents, { isLoading: isUpdating }] = useUpdateConfiguredServiceEventsMutation()
  const [createCSEvents, { isLoading: isCreating }] = useCreateConfiguredServiceEventsMutation()
  const [deleteCSEvents, { isLoading: isDeleting }] = useDeleteConfiguredServiceEventsMutation()
  const [fetchCSEvents] = useLazyFetchConfiguredServiceEventsQuery()

  const isMutationLoading = useMemo(() => isUpdating || isCreating || isDeleting, [isUpdating, isCreating, isDeleting])
  const allowDuplicateEvents = true

  const getAvailableActionsForEvent = useCallback(
    (currentEvent, date, serviceActions, selectedDayEvents) =>
      getAvailableActionsForEventUtil({
        currentEvent,
        date: date || selectedDate,
        serviceActions,
        selectedDayEvents,
        allowDuplicateEvents,
      }),
    [selectedDate, allowDuplicateEvents]
  )

  const handleServiceEvents = async ({
    id,
    itemsMarkedForDeletion: deletionItems,
    isAddingEvent: addingNew,
    newActionId,
    scheduledEvents,
    selectedDate: eventDate,
    editedEvents,
  }) => {
    const deletePromises = Object.keys(deletionItems).map(eventId => {
      return deleteCSEvents({
        id,
        eventId,
      }).unwrap()
    })

    const scheduledEventsById = keyBy(scheduledEvents, 'id')

    let createPromise = Promise.resolve()
    if (addingNew && newActionId) {
      const newEvent = {
        actionId: newActionId,
        serviceDate: formatDateToBEFormatDateFns(eventDate),
      }

      if (isDraft) {
        newEvent.hash = crypto.randomUUID()
      }

      createPromise = createCSEvents({
        id,
        body: newEvent,
      }).unwrap()
    }

    const updatePromises = Object.entries(editedEvents)
      .filter(([eventId]) => editableItems[eventId] && get(deletionItems, eventId, false) === false)
      .map(([eventId, actionId]) => {
        const selectedEvent = scheduledEventsById[eventId]
        return updateCSEvents({
          id,
          eventId,
          body: {
            actionId,
            serviceDate: selectedEvent.serviceDate,
          },
        }).unwrap()
      })

    return Promise.all([...deletePromises, createPromise, ...updatePromises])
  }

  const saveEvents = useCallback(
    async ({ formValues, id, scheduledEvents }) => {
      try {
        const { action } = formValues
        const newActionId = action?.id
        const editedEvents = {}
        Object.keys(formValues.events || {}).forEach(key => {
          const eventAction = formValues.events[key]?.action
          if (eventAction && eventAction.id) {
            editedEvents[key] = eventAction.id
          }
        })

        await handleServiceEvents({
          id,
          scheduledEvents,
          itemsMarkedForDeletion,
          isAddingEvent,
          newActionId,
          selectedDate,
          editedEvents,
        })

        dispatch(resetState())

        if (id) {
          fetchCSEvents({
            id,
          })
        }

        return true
      } catch (error) {
        handleError(error)
        return false
      }
    },
    [
      dispatch,
      isDraft,
      isAddingEvent,
      selectedDate,
      itemsMarkedForDeletion,
      editableItems,
      deleteCSEvents,
      createCSEvents,
      updateCSEvents,
      fetchCSEvents,
    ]
  )

  return {
    saveEvents,
    getAvailableActionsForEvent,
    isMutationLoading,
    updateCSEvents,
    createCSEvents,
    deleteCSEvents,
    fetchCSEvents,
  }
}
